import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import i18n from 'i18next';
import qs from 'qs';

import { environment } from 'environments/environment';

export interface ExtendedAxiosResponse<T = any> {
  ok: boolean;
  data: T;
  status: number;
  statusText: string;
  headers: any;
  config: AxiosRequestConfig;
  request?: any;
  error?: any;
}

export const apiClient = axios.create({
  responseType: 'json',
  withCredentials: true,
  paramsSerializer: qs.stringify,
  timeout: Number(environment.buildProperties.apiRequestTimeoutInSec) * 1000,
});

apiClient.interceptors.request.use((config: AxiosRequestConfig) => {
  // workaround for config.headers.common map // https://github.com/axios/axios/issues/4193
  const axiosRequestCommonHeaders: any = {
    common: {
      'Cache-Control': 'no-cache',
      'Accept-Language': i18n.language || environment.appTranslation.defaultLocale,
      ...(config?.headers?.common as any),
    },
  };

  config.headers = {
    ...config.headers,
    ...axiosRequestCommonHeaders,
  };

  return config;
});

apiClient.interceptors.response.use(
  (response) => normalizeSuccessResponse(response),
  (error) => normalizeErrorResponse(error)
);

const normalizeSuccessResponse = (response: AxiosResponse): ExtendedAxiosResponse => {
  return {
    ...response,
    ok: true,
  };
};

const formatAxiosError = (error: any): ExtendedAxiosResponse => {
  return {
    ok: false,
    data: error.response?.data,
    status: error.response?.status,
    statusText: error.response?.statusText,
    headers: error.response?.headers,
    config: error.config,
    request: error.request,
    error,
  };
};

const normalizeErrorResponse = (error: any): ExtendedAxiosResponse => {
  if (!error.response || error.isAxiosError) {
    // Show general error
    return formatAxiosError(error);
  }

  const { data, status, config } = error.response;

  if (status === 500) {
    // Show general error
  }

  /*
   * Status 401 error code should be handled in src/saga/apiClient/ducks.ts because of saga and action dependency.
   * Other errors can be either handled here, or by extending generator function described.
   */

  if (environment.debug) {
    console.error(
      `Request failed with status code %c${status}\n`,
      'font-weight: bold;',
      `${config.method?.toUpperCase() || '-'} ${config.url}\n\n`,
      'Request:\n',
      config.data,
      '\nResponse:\n',
      data
    );
  }

  return formatAxiosError(error);
};
